import { AccessibleIcon } from '@coop/components';
import { EcoLeafIcon } from '@coop/icons';
import classnames from 'classnames';
import * as React from 'react';

import CloudinaryImage from '../atoms/CloudinaryImage';
import styles from './BaseRecipeTeaser.module.less';

const RecipeRatingContainer = (props: React.PropsWithChildren<{}>) => {
    return <div className={styles.RatingContainer}>{props.children}</div>;
};

const RecipeClimateImpact = (props: { co2ImpactKg: number | null }) => {
    return (
        !!props.co2ImpactKg &&
        props.co2ImpactKg < 2.5 && (
            <AccessibleIcon
                icon={EcoLeafIcon}
                width={24}
                height={24}
                color="dsGreen"
                label={`${Intl.NumberFormat('sv').format(props.co2ImpactKg)} kg co2 ekv / portion`}
            />
        )
    );
};

const RecipeBadges = (props: React.PropsWithChildren<React.ComponentProps<'div'>>) => {
    return <div className={classnames(styles.Badges, props.className)}>{props.children}</div>;
};

type RecipeWrapperProps = {
    name: string;
    imageUrl: string;
    topSlot?: React.ReactNode;
    className?: string;
    withBorder?: boolean;
} & (
    | {
          clickableImage: true;
          onImageClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
      }
    | {
          clickableImage: false;
      }
);

const RecipeWrapper = (
    props: React.PropsWithChildren<RecipeWrapperProps>,
    ref: React.Ref<HTMLDivElement>,
) => {
    return (
        <div
            ref={ref}
            className={classnames(
                styles.Teaser,
                props.withBorder && styles.Border,
                props.className,
            )}
        >
            <div
                className={classnames(
                    styles.ImageContainer,
                    props.clickableImage && styles.Clickable,
                )}
                onClick={props.clickableImage ? props.onImageClick : undefined}
                aria-hidden={!props.clickableImage}
            >
                <CloudinaryImage
                    cssClass={styles.Image}
                    name=""
                    url={props.imageUrl}
                    transform="ar_1.0,f_auto,c_fill,w_300,g_center"
                    retinaTransform="ar_1.0,f_auto,c_fill,w_600,g_center"
                    lazy
                    fallbackUrl="/Assets/Images/missing-image-product.png"
                />
            </div>
            <div className={styles.Bottom}>{props.children}</div>
            {props.topSlot && <div className={styles.TopActions}>{props.topSlot}</div>}
        </div>
    );
};

const RecipeContent = (props: { children: React.ReactNode; className?: string }) => {
    return <div className={classnames(styles.Content, props.className)}>{props.children}</div>;
};

const RecipeInfo = (props: { children: React.ReactNode }) => {
    return <div className={styles.Info}>{props.children}</div>;
};

type RecipeHeadingProps = { flexOrder?: number } & (
    | (React.ButtonHTMLAttributes<HTMLParagraphElement> & {
          as: 'p';
      })
    | (React.AnchorHTMLAttributes<HTMLAnchorElement> & {
          as: 'a';
      })
);

const RecipeHeading = (props: RecipeHeadingProps) => {
    if (props.as === 'a') {
        return (
            <a
                className={classnames(styles.Heading, styles.Link)}
                style={{ order: props.flexOrder }}
                href={props.href}
                onClick={props.onClick}
                tabIndex={-1}
            >
                {props.children}
            </a>
        );
    }

    return (
        <p className={styles.Heading} style={{ order: props.flexOrder }}>
            {props.children}
        </p>
    );
};

const RecipeActions = (props: { children: React.ReactNode }) => {
    return <div className={styles.Actions}>{props.children}</div>;
};

const BaseRecipeTeaser = {
    Wrapper: React.forwardRef(RecipeWrapper),
    Badges: RecipeBadges,
    RatingContainer: RecipeRatingContainer,
    ClimateImpact: RecipeClimateImpact,
    Content: RecipeContent,
    Heading: RecipeHeading,
    Actions: RecipeActions,
    Info: RecipeInfo,
};

export default BaseRecipeTeaser;
